import { AppIcon, type IconName } from '@/components/ui/icons';
import React from 'react';
import type { User as AxUser } from '../types';
import { getEmploymentType, shouldUseEmploymentType } from '../lib/auth/employment';
import { ROLE_DISPLAY, type RoleDisplayKey } from '../lib/auth/projectRoleDisplay';
import {
  getMvpAccountRoleLabel,
} from '../lib/auth/accountRoles';
import { resolveIdentityRoles } from '../lib/auth/identityRoles';

interface UserBadgeProps {
  user?: Partial<AxUser> | null;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  variant?: 'badge' | 'chip' | 'minimal' | 'glass';
}


const UserBadge: React.FC<UserBadgeProps> = ({
  user,
  size = 'md',
  showIcon = true,
  showLabel = true,
  variant = 'badge'
}) => {
  const identity = resolveIdentityRoles(user);
  const accountRole = identity.platformRole ?? identity.accountRole;
  const displayRole: RoleDisplayKey = accountRole === 'organization_owner' ? 'platform_owner'
    : accountRole === 'support_operator' ? 'support_admin'
      : accountRole ?? 'organization_member';
  const config = ROLE_DISPLAY[displayRole];
  const displayLabel = accountRole ? getMvpAccountRoleLabel(accountRole) : 'Role unavailable';
  const _IconComponent = config.icon;

  const sizeClasses = {
    sm: {
      container: 'px-2 py-0.5 text-[11px] leading-4',
      icon: 'w-3 h-3',
      iconContainer: 'w-4 h-4'
    },
    md: {
      container: 'px-2.5 py-1 text-xs',
      icon: 'w-3.5 h-3.5',
      iconContainer: 'w-5 h-5'
    },
    lg: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      iconContainer: 'w-6 h-6'
    }
  };

  const currentSize = sizeClasses[size];

  // Roles are identity metadata, not workflow status. Keep them neutral so
  // semantic colour remains available for risk, action, and completion states.
  const roleClasses = 'border-border-subtle text-text-secondary bg-surface-muted/70';

  // New glass variant - premium 2026 style (minimal, no bg)
  if (variant === 'glass') {
    return (
      <div className={`inline-flex items-center gap-1 ${currentSize.container} ${roleClasses} rounded-pill border font-medium`}>
        {showIcon && <AppIcon name={config.icon as IconName} className={currentSize.icon} />}
        {showLabel && <span>{displayLabel}</span>}
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className="flex items-center space-x-1">
        {showIcon && (
          <div className={`${currentSize.iconContainer} ax-icon-frame ax-icon-frame--neutral rounded-full`}>
            <AppIcon name={config.icon as IconName} className={currentSize.icon} />
          </div>
        )}
        {showLabel && (
          <span className="font-medium text-text-secondary">
            {displayLabel}
          </span>
        )}
      </div>
    );
  }

  if (variant === 'chip') {
    const showEmployment = shouldUseEmploymentType(user || undefined);
    const employmentType = showEmployment ? getEmploymentType(user || undefined) : null;
    const empLabel = employmentType === 'contractor' ? 'Contractor' : employmentType === 'external' ? 'External' : 'Internal';

    return (
      <div className={`inline-flex items-center gap-1.5 ${currentSize.container} ${roleClasses} rounded-pill border font-medium`}>
        {showIcon && <AppIcon name={config.icon as IconName} className={currentSize.icon} />}
        {showLabel && <span>{displayLabel}</span>}
        {showEmployment && (
          <span className="ax-badge ax-badge--neutral ax-badge--xs ml-1">
            {empLabel}
          </span>
        )}
      </div>
    );
  }

  // Default badge variant - updated premium style
  const showEmployment = shouldUseEmploymentType(user || undefined);
  const employmentType = showEmployment ? getEmploymentType(user || undefined) : null;
  const empLabel = employmentType === 'contractor' ? 'Contractor' : employmentType === 'external' ? 'External' : 'Internal';

  return (
    <div className={`inline-flex items-center gap-1.5 ${currentSize.container} ${roleClasses} rounded-pill border font-medium`}>
      {showIcon && <AppIcon name={config.icon as IconName} className={currentSize.icon} />}
      {showLabel && <span>{displayLabel}</span>}
      {showEmployment && (
        <span className="ax-badge ax-badge--neutral ax-badge--xs ml-1">
          {empLabel}
        </span>
      )}
    </div>
  );
};

export default UserBadge;
